import {
  ClickAwayListener,
  ListItemButton,
  Popper,
  Typography,
} from '@mui/material'
import { memo, useCallback } from 'react'
import styled from 'styled-components'
import { colorPalette } from '../../../../style/colorPalette'
import { intl } from '../../../../../i18n'
import { SortConfig, SortItem } from '../../hooks/useSort'
import AscIcon from '../../../../components/icons/AscIcon'
import DescIcon from '../../../../components/icons/DescIcon'
import useAnchorButton from '../Filter/useAnchorButton'
import NavigateNextRoundedIcon from '@mui/icons-material/NavigateNextRounded'
import { ActiveDot } from '../shared/ActiveDot'

export type Props = {
  applySort: (sortConfig: SortConfig | null) => void
  currentSortConfig: SortConfig | null
}

export const SortButton = memo(({ applySort, currentSortConfig }: Props) => {
  const { isOpen, anchorEl, handleClose, toggleOpen } = useAnchorButton()
  const applySortWithClose = useCallback(
    (sortConfig: SortConfig | null) => {
      applySort(sortConfig)
      handleClose()
    },
    [applySort, handleClose]
  )
  const isSortActive = !!currentSortConfig
  return (
    <ClickAwayListener onClickAway={handleClose}>
      <div>
        <StyledListItemButton onClick={toggleOpen}>
          <StyledSortLabelTypography variant={'body2'}>
            {intl.formatMessage({ id: 'toolbar.label.sort' })}
          </StyledSortLabelTypography>
          {isSortActive && <ActiveDot />}
          <StyledNavigateNextRoundedIcon isOpen={isOpen} />
        </StyledListItemButton>
        {isOpen && (
          <SortDialog
            anchorEl={anchorEl}
            applySort={applySortWithClose}
            currentSortConfig={currentSortConfig}
          />
        )}
      </div>
    </ClickAwayListener>
  )
})

const StyledSortLabelTypography = styled(Typography)({
  minWidth: 'fit-content',
  marginRight: '0px',
})
const StyledListItemButton = styled(ListItemButton)({
  borderRadius: '4px',
  minWidth: 'fit-content',
  color: colorPalette.monotone[4],
})
const StyledNavigateNextRoundedIcon = styled(NavigateNextRoundedIcon)<{
  isOpen: boolean
}>(props => ({
  marginLeft: '5px',
  color: colorPalette.monotone[4],
  cursor: 'pointer',
  transform: props.isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
}))

const SortDialog = memo(
  ({
    anchorEl,
    applySort,
    currentSortConfig,
  }: {
    anchorEl: Element | undefined
    applySort: (sortConfig: SortConfig | null) => void
    currentSortConfig: SortConfig | null
  }) => {
    const isSortActive = !!currentSortConfig
    return (
      <StyledPopper open={true} anchorEl={anchorEl} placement={'bottom-start'}>
        <ResetSortButton
          onClick={() => applySort(null)}
          disabled={!isSortActive}
        >
          {intl.formatMessage({ id: 'kanban.sort.reset' })}
        </ResetSortButton>
        <SortItemButton
          sortItem={'scheduledDate.startDate'}
          applySort={applySort}
          currentSortConfig={currentSortConfig}
        />
        <SortItemButton
          sortItem={'scheduledDate.endDate'}
          applySort={applySort}
          currentSortConfig={currentSortConfig}
        />
        <SortItemButton
          sortItem={'priority'}
          applySort={applySort}
          currentSortConfig={currentSortConfig}
        />
        <SortItemButton
          sortItem={'responsible.name'}
          applySort={applySort}
          currentSortConfig={currentSortConfig}
        />
      </StyledPopper>
    )
  }
)

const StyledPopper = styled(Popper)({
  boxShadow: '0px 4px 16px 0px #7B8CAA80',
  background: colorPalette.monotone[0],
  display: 'flex',
  margin: '10px',
  borderRadius: '4px',
  zIndex: 1300,
  flexDirection: 'column',
  padding: '16px',
  gap: '12px',
})

const ResetSortButton = styled('button')<{ disabled: boolean }>(
  ({ disabled }) => ({
    backgroundColor: 'transparent',
    border: 'none',
    cursor: disabled ? 'default' : 'pointer',
    padding: '0',
    color: disabled ? colorPalette.monotone[3] : colorPalette.pink[6],
    fontSize: '12px',
    fontWeight: 400,
    textAlign: 'left',
    '&:hover': {
      backgroundColor: disabled ? 'transparent' : colorPalette.monotone[1],
    },
  })
)

const SortItemButton = memo(
  ({
    sortItem,
    applySort,
    currentSortConfig,
  }: {
    sortItem: SortItem
    applySort: (sortConfig: SortConfig | null) => void
    currentSortConfig: SortConfig | null
  }) => {
    const title = intl.formatMessage({ id: `kanban.sort.itemname.${sortItem}` })
    const isAscActive =
      currentSortConfig?.key === sortItem && currentSortConfig?.isAsc === true
    const isDescActive =
      currentSortConfig?.key === sortItem && currentSortConfig?.isAsc === false
    return (
      <ItemContainer>
        <StyledSortLabelTypography>{title}</StyledSortLabelTypography>
        <AscDescContainer>
          <AscDescButton
            isActive={isAscActive}
            onClick={() => applySort({ key: sortItem, isAsc: true })}
          >
            <AscIcon />
          </AscDescButton>
          <AscDescButton
            isActive={isDescActive}
            onClick={() => applySort({ key: sortItem, isAsc: false })}
          >
            <DescIcon />
          </AscDescButton>
        </AscDescContainer>
      </ItemContainer>
    )
  }
)

const ItemContainer = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '12px',
  justifyContent: 'space-between',
})
const AscDescContainer = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '4px',
})
const AscDescButton = styled('button')<{ isActive?: boolean }>(
  ({ isActive }) => ({
    backgroundColor: isActive
      ? colorPalette.skyBlue[0]
      : colorPalette.monotone[0],
    border: isActive ? `1px solid ${colorPalette.skyBlue[7]}` : 'none',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: isActive
        ? colorPalette.skyBlue[1]
        : colorPalette.monotone[2],
    },
    padding: '8px',
    borderRadius: '4px',
  })
)
